'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { ArrowRight, ArrowLeft, Lock } from 'lucide-react';
import { motion } from 'framer-motion';

interface SubInfoProps {
  pageKey?: string;
  buttons?: Array<{
    title: string;
    url: string;
    active: boolean;
  }>;
}

interface BottomNavbarProps {
  hideUpcoming?: boolean;
  hideVisions?: boolean;
  subInfo?: SubInfoProps;
}

interface BottomNavButtonProps {
  href: string;
  className?: string;
  children: React.ReactNode;
  onClick?: () => void;
}

// Component for bottom navbar buttons with strobing effect
function BottomNavButton({ href, className, children, onClick }: BottomNavButtonProps) {
  const [isHovered, setIsHovered] = useState(false);

  // Define the strobing animation variants
  const strobingVariants = {
    initial: { filter: 'invert(0%)' },
    hover: {
      filter: ['invert(0%)', 'invert(100%)', 'invert(0%)', 'invert(100%)', 'invert(0%)'],
      transition: {
        duration: 0.6,
        times: [0, 0.25, 0.5, 0.75, 1],
        ease: 'easeInOut',
      },
    },
  };

  return (
    <motion.div initial="initial" animate={isHovered ? 'hover' : 'initial'} variants={strobingVariants}>
      <Link
        href={href}
        className={`nav-button inline-flex items-center ${className || ''}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={onClick}
      >
        {children}
      </Link>
    </motion.div>
  );
}

export function BottomNavbar({ hideUpcoming = false, hideVisions = false, subInfo = {} }: BottomNavbarProps) {
  const [showCollectorMenu, setShowCollectorMenu] = useState(false);

  const toggleCollectorMenu = () => {
    setShowCollectorMenu(!showCollectorMenu);
  };

  return (
    <div className="px-2 pt-0 pb-2 main-bottom-navbar">
      <div className="flex justify-between items-center">
        <div className="flex-1 flex">
          {!hideVisions && (
            <BottomNavButton href="/catalog/drops/visions" className="hidden lg:flex hide-mobile">
              {!hideUpcoming && <span className="text-gray-500 mr-1">UPCOMING</span>}
              <span className="mr-1">VISIONS</span>
              <ArrowRight size={16} />
            </BottomNavButton>
          )}
          <div className="ml-2"></div>
          <BottomNavButton href="/updates">
            <span>FEED</span>
          </BottomNavButton>
        </div>

        <div className="flex-1 flex items-center justify-center gap-x-4" id="addon-nav">
          {subInfo.pageKey === 'collection-details' && (
            <>
              {subInfo.buttons?.map((button, index) => (
                <BottomNavButton key={index} href={button.url} className={`${button.active ? ' text-gray-300' : ''}`}>
                  <span>{button.title}</span>
                </BottomNavButton>
              ))}
            </>
          )}
        </div>
        <div className="flex-1 flex items-center">
          {/*
          æ
          <div className="flex items-center">
            {showCollectorMenu && (
              <div className="flex items-center">
                <BottomNavButton href="/collector">
                  <span>DASHBOARD</span>
                </BottomNavButton>
                <div className="ml-2"></div>
                <BottomNavButton href="/settings">
                  <span>SETTINGS</span>
                </BottomNavButton>
                <div className="ml-2"></div>
                <BottomNavButton href="/auth/logout">
                  <span>DISCONNECT</span>
                </BottomNavButton>
                <div className="ml-2"></div>
                <BottomNavButton href="/auth/signin">
                  <span>connect</span>
                </BottomNavButton>
                <div className="ml-2"></div>
                <BottomNavButton href="/collector/onboarding">
                  <span>onboarding</span>
                </BottomNavButton>
                <div className="ml-2"></div>
              </div>
            )}
            <BottomNavButton href="#" onClick={toggleCollectorMenu}>
              <Lock size={7} className="mr-1" />
              <span>COLLECTOR ACCESS</span>
            </BottomNavButton>
          </div>
        */}
        </div>
      </div>
    </div>
  );
}
